import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { api } from "../../lib/api";
import { AdminGate } from "../events/AdminGate";
import { fmtNum, fmtQty } from "./kitchenUtils";

const unitWord = (ln) => (ln.pan_label === "half pan" ? "half pan" : ln.pan_label === "full pan" ? "full pan" : ln.pan_label);

const IngredientCells = ({ ln, g }) => (
  <>
    <td className="p-1.5 border border-black/30">{g.name}</td>
    <td className="p-1.5 border border-black/30 tabular-nums">
      {fmtQty(g.per_unit, g.unit, g.count_label)}{g.cut_into ? ` → ${fmtNum(g.cut_per_unit)} ${g.cut_label}` : ""}
      <span className="text-[10px] text-black/50"> / {unitWord(ln)}</span>
    </td>
    <td className="p-1.5 border border-black/30 tabular-nums font-bold">
      {fmtQty(g.total, g.unit, g.count_label)}{g.cut_into ? ` → ${fmtNum(g.cut_total)} ${g.cut_label}` : ""}
    </td>
    <td className="p-1.5 border border-black/30 w-10" />
  </>
);

const ItemBlock = ({ ln }) => (
  <tbody className="break-inside-avoid">
    <tr className="bg-black/5">
      <td colSpan={4} className="p-1.5 border border-black/30">
        <b>{ln.name}</b> — {fmtNum(ln.quantity)} {ln.has_recipe ? `${unitWord(ln)}${ln.quantity === 1 || ln.pan_label === "person" ? "" : "s"}` : "×"}
        {ln.servings ? ` · serves ~${fmtNum(ln.servings)}` : ""}{ln.options ? ` · ${ln.options}` : ""}
        {ln.portion_note ? <span className="block text-[11px]">Portion: {ln.portion_note}</span> : null}
        {ln.prep_notes ? <span className="block text-[11px]">Notes: {ln.prep_notes}</span> : null}
        {!ln.has_recipe && <span className="block text-[11px] italic">No recipe set up — prepare as usual.</span>}
      </td>
    </tr>
    {ln.ingredients.map((g) => <tr key={g.ingredient_id}><IngredientCells ln={ln} g={g} /></tr>)}
  </tbody>
);

const SheetTable = ({ lines }) => (
  <table className="w-full border-collapse text-[12px] mt-2">
    <thead><tr className="text-left"><th className="p-1.5 border border-black/30">Ingredient</th><th className="p-1.5 border border-black/30">Per pan / unit</th><th className="p-1.5 border border-black/30">Total</th><th className="p-1.5 border border-black/30">✓</th></tr></thead>
    {lines.map((ln, i) => <ItemBlock key={`${ln.package_id}-${i}`} ln={ln} />)}
  </table>
);

const OrderSheet = ({ o }) => (
  <section className="break-after-page mb-8" data-testid={`print-order-${o.id}`}>
    <h2 className="text-[18px] font-black">{o.organization || o.customer_name} <span className="font-normal text-[13px]">{o.invoice_number || o.order_number || ""}{o.drop_label ? ` · ${o.drop_label}` : ""}</span></h2>
    <p className="text-[12px]">{o.event_date} {o.event_time || ""} · {o.fulfillment}{o.address ? ` · ${o.address}${o.city ? `, ${o.city}` : ""}` : ""}{o.guests ? ` · ${o.guests} guests` : ""} · status: {o.status.replace(/_/g, " ")}</p>
    {o.notes && <p className="text-[12px] mt-1"><b>Customer notes:</b> {o.notes}</p>}
    <SheetTable lines={o.lines} />
  </section>
);

// Combined day sheet: same item rows merged across orders, plus raw totals.
const combine = (orders) => {
  const byItem = {};
  orders.forEach((o) => o.lines.forEach((ln) => {
    const key = `${ln.package_id}|${ln.pan_label || ""}|${ln.options}`;
    if (!byItem[key]) byItem[key] = { ...ln, quantity: 0, servings: 0, ingredients: ln.ingredients.map((g) => ({ ...g, total: 0, cut_total: 0 })) };
    const t = byItem[key];
    t.quantity += ln.quantity;
    t.servings += ln.servings || 0;
    ln.ingredients.forEach((g, i) => { t.ingredients[i].total += g.total; t.ingredients[i].cut_total += g.cut_total || 0; });
  }));
  return Object.values(byItem).sort((a, b) => a.name.localeCompare(b.name));
};

function PrintPrepInner() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const date = params.get("date");
  const mode = params.get("mode") || "orders";
  const ids = useMemo(() => new Set((params.get("ids") || "").split(",").filter(Boolean)), [params]);

  const printed = useRef(false);
  useEffect(() => {
    api.get("/admin/kitchen/prep", { params: { date } }).then((r) => setData(r.data)).catch(() => setData({ orders: [], error: true }));
  }, [date]);
  useEffect(() => {
    if (!data || data.error || printed.current) return undefined;
    const t = setTimeout(() => { printed.current = true; window.print(); }, 400);
    return () => clearTimeout(t);
  }, [data]);

  if (!data) return <p className="p-6">Loading…</p>;
  if (data.error) return <p className="p-6" data-testid="print-prep-error">Couldn't load the prep sheet. Check your connection and reload.</p>;
  const orders = data.orders.filter((o) => !ids.size || ids.has(o.sheet_key));
  return (
    <div className="bg-white text-black p-6 max-w-[800px] mx-auto" data-testid="print-prep">
      <div className="flex items-center justify-between print:hidden mb-4">
        <p className="text-[13px]">Kitchen prep — {date}</p>
        <button type="button" onClick={() => window.print()} data-testid="print-prep-button" className="h-10 px-4 rounded-full bg-black text-white text-[12px] font-bold inline-flex items-center gap-2"><Printer className="h-4 w-4" /> Print</button>
      </div>
      <h1 className="text-[22px] font-black">Fresh Fast Foods — Kitchen Production Sheet</h1>
      <p className="text-[12px] mb-4">{date} · {orders.length} order{orders.length === 1 ? "" : "s"} · printed {new Date().toLocaleString()}</p>
      {mode === "combined" ? (
        <section data-testid="print-combined">
          <h2 className="text-[16px] font-black">All orders combined</h2>
          <p className="text-[11px]">{orders.map((o) => o.organization || o.customer_name).join(" · ")}</p>
          <SheetTable lines={combine(orders)} />
        </section>
      ) : (
        orders.map((o) => <OrderSheet key={o.sheet_key} o={o} />)
      )}
    </div>
  );
}

export default function PrintPrepPage() {
  return <AdminGate><PrintPrepInner /></AdminGate>;
}
